/**
 * Inline PDF preview for Coworker artifact Files (web): renders the CDN
 * object directly in an iframe card under the assistant message — the
 * document equivalent of the inline image thumbnails. Native has no
 * iframe; there the file card opens the full-screen in-app PdfViewer.
 */
import React, { useMemo } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useOnScreen } from '@/hooks/useOnScreen';

const isPdf = (file) => {
  const mime = (file?.mimeType || '').toLowerCase();
  const name = (file?.name || '').toLowerCase();
  return mime === 'application/pdf' || name.endsWith('.pdf');
};

// Each mounted PDF iframe spins up Chrome's PDFium viewer with its own
// document buffer, so a thread that produced a dozen deliverables was holding
// a dozen viewers at once. Mount only the ones on (or near) screen and tear
// the rest down; the header card stays put so scroll position never shifts.
function PdfPreviewCard({ file, border, colors }) {
  const [containerRef, isOnScreen] = useOnScreen({ rootMargin: '300px' });

  return (
    <View ref={containerRef} style={[styles.card, { borderColor: border }]}>
      <View style={[styles.header, { borderBottomColor: border, backgroundColor: colors.solid_color_block }]}>
        <Icon name="file-pdf" size={13} color={colors.text_color_secondary} />
        <Text size="xxs" color="secondary" numberOfLines={1} style={{ flex: 1, marginLeft: 6 }}>
          {file.name || 'document.pdf'}
        </Text>
        {/* #toolbar=0 keeps the embed compact; the click-to-open card
            below still routes to the full pdf-preview sidebar. */}
      </View>
      {isOnScreen ? (
        <iframe
          title={file.name || 'document.pdf'}
          src={`${file.cdnPath}#toolbar=0&navpanes=0&view=FitH`}
          style={styles.frame}
        />
      ) : (
        <View style={[styles.framePlaceholder, { backgroundColor: colors.solid_color_block }]} />
      )}
    </View>
  );
}

export default function CoworkerDocumentPreview({ files }) {
  const { colors } = useTheme();
  const border = colors.border_color_secondary;

  const pdfs = useMemo(
    () => (Array.isArray(files) ? files.filter((f) => f && isPdf(f) && f.cdnPath) : []),
    [files],
  );

  if (Platform.OS !== 'web' || pdfs.length === 0) return null;

  return (
    <View style={styles.container}>
      {pdfs.map((file) => (
        <PdfPreviewCard
          key={file.id || file.cdnPath}
          file={file}
          border={border}
          colors={colors}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
    marginTop: 8,
    maxWidth: 440,
  },
  card: {
    borderRadius: 10,
    borderWidth: 1,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderBottomWidth: 1,
  },
  frame: {
    width: '100%',
    height: 380,
    border: 'none',
    display: 'block',
    backgroundColor: '#fff',
  },
  // Same height as the iframe so mounting / unmounting never shifts scroll.
  framePlaceholder: {
    width: '100%',
    height: 380,
  },
});
